// Helpers de geocoding (Nominatim/OpenStreetMap) y ruteo
// Nominatim es gratis con un rate limit de 1 req/seg.

import { supabase } from './supabase'
import { BARRIOS_MVD } from '../data/barriosMvdGeo'

const NOMINATIM = 'https://nominatim.openstreetmap.org/search'
const HEADERS = { 'User-Agent': 'Limpack/1.0 (hola@limpack.uy)' }

/** Geocodifica una dirección de Montevideo.
 *  Devuelve { lat, lng, display, barrioDetectado } o null.
 *  `barrioDetectado` es el nombre del barrio según OpenStreetMap (ej "Pocitos", "Punta Carretas"). */
export async function geocodificar(direccion, ciudad = 'Montevideo, Uruguay') {
  if (!direccion?.trim()) return null
  const q = `${direccion}, ${ciudad}`
  try {
    const url = `${NOMINATIM}?format=json&limit=1&countrycodes=uy&addressdetails=1&q=${encodeURIComponent(q)}`
    const r = await fetch(url, { headers: HEADERS })
    if (!r.ok) return null
    const data = await r.json()
    if (!data?.[0]) return null
    const item = data[0]
    const addr = item.address || {}
    // OSM puede devolver el barrio en distintos campos según la zona
    const barrioDetectado = addr.suburb || addr.neighbourhood || addr.quarter || addr.city_district || null
    return {
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
      display: item.display_name,
      barrioDetectado,
      address: addr,
    }
  } catch (e) {
    console.error('geocodificar error:', e)
    return null
  }
}

/** Autocomplete de direcciones en TODO Uruguay (Nominatim), pensado para el desplegable
 *  del checkout mientras el cliente escribe. Devuelve hasta `limite` resultados con coords.
 *  No fuerza ciudad (a diferencia de geocodificar) para que ande en Canelones e interior.
 *  Ojo con el rate limit de Nominatim (1 req/seg): llamar con debounce desde la UI. */
export async function buscarDirecciones(texto, limite = 6) {
  const t = (texto || '').trim()
  if (t.length < 4) return []
  try {
    const url = `${NOMINATIM}?format=json&limit=${limite}&countrycodes=uy&addressdetails=1&q=${encodeURIComponent(t)}`
    const r = await fetch(url, { headers: HEADERS })
    if (!r.ok) return []
    const data = await r.json()
    return (data || []).map(item => {
      const a = item.address || {}
      const calle = a.road || a.pedestrian || a.footway || a.neighbourhood || null
      const localidad = a.city || a.town || a.village || a.suburb || a.county || null
      return {
        lat: parseFloat(item.lat),
        lng: parseFloat(item.lon),
        display: item.display_name,
        calle,
        numero: a.house_number || null,
        localidad,
        address: a,
      }
    }).filter(x => Number.isFinite(x.lat) && Number.isFinite(x.lng))
  } catch (e) {
    console.error('buscarDirecciones error:', e)
    return []
  }
}

/** Reverse geocoding: dado un punto (lat,lng), devuelve la dirección más cercana.
 *  Ideal para cuando el usuario arrastra el pin en el mapa — le mostramos qué calle
 *  y número dice OSM que está ahí.
 *  Devuelve { calle, numero, barrio, display, address } o null. */
export async function reverseGeocodificar(lat, lng) {
  if (lat == null || lng == null) return null
  try {
    const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`
    const r = await fetch(url, { headers: HEADERS })
    if (!r.ok) return null
    const data = await r.json()
    if (!data?.address) return null
    const addr = data.address
    const calle = addr.road || addr.pedestrian || addr.footway || null
    const numero = addr.house_number || null
    const barrio = addr.suburb || addr.neighbourhood || addr.quarter || addr.city_district || null
    return {
      calle,
      numero,
      barrio,
      display: data.display_name,
      address: addr,
      // Texto formateado tipo "18 de Julio 1234" listo para pegar al input
      direccionFormateada: calle ? (numero ? `${calle} ${numero}` : calle) : null,
    }
  } catch (e) {
    console.error('reverseGeocodificar error:', e)
    return null
  }
}

/** Normaliza un nombre de barrio para comparación: lowercase, sin acentos, sin espacios. */
function normalizarBarrio(nombre) {
  if (!nombre) return ''
  return nombre
    .toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .trim()
}

/** Normalización "base": además del normalizarBarrio, le saca sufijos comunes
 *  (norte/sur/este/oeste/nuevo/viejo/alto/bajo/chico/grande) para que sub-barrios
 *  matcheen con su barrio padre. Ej:
 *    "Malvín Nuevo" → "malvin" matchea "Malvín"
 *    "Carrasco Norte" → "carrasco" matchea "Carrasco"
 *    "Pocitos Nuevo" → "pocitos" matchea "Pocitos"
 *  Solo lo hace si lo que queda tiene al menos 4 caracteres (para no destrozar nombres cortos). */
function normalizarBarrioBase(nombre) {
  let n = normalizarBarrio(nombre)
  const sufijos = ['norte', 'sur', 'este', 'oeste', 'nuevo', 'nueva', 'viejo', 'vieja', 'alto', 'bajo', 'chico', 'grande']
  for (const s of sufijos) {
    if (n.endsWith(s) && n.length - s.length >= 4) {
      n = n.slice(0, -s.length)
      break
    }
  }
  return n
}

/**
 * Compara el barrio detectado por el geocoder con el barrio elegido por el cliente,
 * y con la lista de barrios activos del sistema.
 *
 * @param {string|null} barrioDetectado Nombre que devolvió OSM
 * @param {string} barrioElegidoNombre Barrio que eligió el cliente del dropdown
 * @param {Array<{id, nombre}>} barriosActivos Lista de barrios cubiertos
 *
 * @returns {{ ok:boolean, sugerenciaActivo:object|null, sinCobertura:boolean, mensaje:string }}
 *   - ok=true si el barrio detectado coincide con el elegido
 *   - sugerenciaActivo: si OSM detectó OTRO barrio que SÍ cubrimos → sugerimos cambiar
 *   - sinCobertura=true si OSM detectó un barrio que NO cubrimos
 */
export function validarBarrioConDireccion(barrioDetectado, barrioElegidoNombre, barriosActivos) {
  if (!barrioDetectado) {
    return { ok: true, sugerenciaActivo: null, sinCobertura: false, mensaje: '' }
  }
  const normDet = normalizarBarrio(barrioDetectado)
  const normEle = normalizarBarrio(barrioElegidoNombre)
  if (normDet === normEle) {
    return { ok: true, sugerenciaActivo: null, sinCobertura: false, mensaje: '' }
  }
  // Match por "base" — para que "Carrasco Norte"/"Carrasco Sur" matcheen con "Carrasco"
  // (pero "Malvín Norte" NO matchee con "Carrasco" porque sus bases son distintas).
  const baseDet = normalizarBarrioBase(barrioDetectado)
  const baseEle = normalizarBarrioBase(barrioElegidoNombre)
  if (baseDet && baseDet === baseEle) {
    return { ok: true, sugerenciaActivo: null, sinCobertura: false, mensaje: '' }
  }
  // No coinciden — buscar si el detectado es uno de los barrios activos (exacto, después por base)
  let sugerencia = (barriosActivos || []).find(b => normalizarBarrio(b.nombre) === normDet)
  if (!sugerencia) {
    sugerencia = (barriosActivos || []).find(b => {
      const baseB = normalizarBarrioBase(b.nombre)
      return baseB && baseB === baseDet
    })
  }
  if (sugerencia) {
    return {
      ok: false,
      sugerenciaActivo: sugerencia,
      sinCobertura: false,
      mensaje: `Esta dirección parece estar en ${sugerencia.nombre}, no en ${barrioElegidoNombre}.`,
    }
  }
  // No es un barrio activo — fuera de cobertura
  return {
    ok: false,
    sugerenciaActivo: null,
    sinCobertura: true,
    mensaje: `Esta dirección parece estar en ${barrioDetectado}, una zona donde por ahora no llegamos.`,
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Validación de barrio por COORDENADAS (point-in-polygon contra los límites
// oficiales de Montevideo). Reemplaza la validación por NOMBRE de OSM, que fallaba
// cuando el geocoder no devolvía el barrio (caso Bernardina Fragoso de Rivera 1414:
// OSM daba las coords pero sin `suburb` → validarBarrioConDireccion devolvía ok:true
// y el checkout no bloqueaba). Con los polígonos, el barrio del pin es determinístico.
// ─────────────────────────────────────────────────────────────────────────────

/** Normaliza un nombre de barrio a MAYÚSCULAS sin acentos, para comparar. */
function normNombreOficial(s) {
  return (s || '').toUpperCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim()
}

// Barrios activos cuyo nombre NO coincide textual con el oficial del padrón, o que
// no existen como barrio oficial y hay que mapear a los que los contienen.
//   'Parque Batlle' → oficial 'PQUE BATLLE VILLA DOLORES'
//   'Villa Biarritz' NO es barrio oficial (está dentro de Punta Carretas / Pocitos) →
//      se acepta si el pin cae en cualquiera de esos dos.
const ALIAS_OFICIAL = {
  'PARQUE BATLLE': ['PQUE BATLLE VILLA DOLORES'],
  'VILLA BIARRITZ': ['POCITOS', 'PUNTA CARRETAS'],
}
function oficialesAceptables(nombreActivo) {
  const n = normNombreOficial(nombreActivo)
  return ALIAS_OFICIAL[n] || [n]
}

/** Ray-casting: ¿el punto (lng,lat) está dentro del anillo? */
function puntoEnAnillo(x, y, ring) {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][0], yi = ring[i][1], xj = ring[j][0], yj = ring[j][1]
    if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / ((yj - yi) || 1e-12) + xi)) inside = !inside
  }
  return inside
}

/** Devuelve el nombre oficial (normalizado) del barrio que contiene el punto, o null. */
export function barrioOficialDeCoords(lat, lng) {
  if (lat == null || lng == null) return null
  for (const b of BARRIOS_MVD) {
    for (const ring of b.r) {
      if (puntoEnAnillo(lng, lat, ring)) return b.n
    }
  }
  return null
}

/**
 * Valida el barrio elegido contra la ubicación REAL del pin (point-in-polygon).
 * Mismo shape que validarBarrioConDireccion para que el checkout no cambie su UI.
 *   - ok=true si el barrio del pin coincide con el elegido.
 *   - sugerenciaActivo: si el pin cae en OTRO barrio que SÍ cubrimos.
 *   - sinCobertura=true si el pin cae en un barrio que NO cubrimos.
 *   - indeterminado=true (ok:true) si el punto no cae en ningún polígono conocido
 *     (fuera de Montevideo o hueco de simplificación) → el checkout cae a la validación
 *     por nombre como respaldo, para no bloquear de más.
 */
export function validarBarrioPorCoords(lat, lng, barrioElegidoNombre, barriosActivos) {
  const oficial = barrioOficialDeCoords(lat, lng)
  if (!oficial) {
    return { ok: true, sugerenciaActivo: null, sinCobertura: false, mensaje: '', indeterminado: true }
  }
  if (oficialesAceptables(barrioElegidoNombre).includes(oficial)) {
    return { ok: true, sugerenciaActivo: null, sinCobertura: false, mensaje: '' }
  }
  // El pin cae en otro barrio: ¿lo cubrimos? Preferir match directo de nombre sobre alias.
  let sugerencia = (barriosActivos || []).find(b => normNombreOficial(b.nombre) === oficial)
  if (!sugerencia) sugerencia = (barriosActivos || []).find(b => oficialesAceptables(b.nombre).includes(oficial))
  if (sugerencia) {
    return {
      ok: false,
      sugerenciaActivo: sugerencia,
      sinCobertura: false,
      mensaje: `Esta dirección está en ${sugerencia.nombre}, no en ${barrioElegidoNombre}.`,
    }
  }
  const bonito = oficial.split(' ').map(w => w.charAt(0) + w.slice(1).toLowerCase()).join(' ')
  return {
    ok: false,
    sugerenciaActivo: null,
    sinCobertura: true,
    mensaje: `Esta dirección está en ${bonito}, una zona donde por ahora no llegamos.`,
  }
}

/** Geocodea muchos clientes que no tienen lat/lng todavía, respetando rate limit. */
export async function geocodearClientesPendientes(clientes) {
  const pendientes = clientes.filter(c => !c.lat || !c.lng)
  const resultado = { ok: 0, fail: 0 }
  for (const c of pendientes) {
    const geo = await geocodificar(`${c.direccion}, ${c.barrio || ''}`)
    if (geo) {
      await supabase.rpc('set_cliente_geo', { cliente_id: c.id, lat_p: geo.lat, lng_p: geo.lng })
      c.lat = geo.lat; c.lng = geo.lng
      resultado.ok++
    } else {
      resultado.fail++
    }
    await new Promise(r => setTimeout(r, 1100)) // rate limit Nominatim
  }
  return resultado
}

/** Distancia haversine entre dos coords en metros. */
export function distanciaMetros(a, b) {
  const R = 6371000
  const toRad = (x) => x * Math.PI / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat), lat2 = toRad(b.lat)
  const x = Math.sin(dLat/2)**2 + Math.sin(dLng/2)**2 * Math.cos(lat1) * Math.cos(lat2)
  return 2 * R * Math.asin(Math.sqrt(x))
}

/**
 * Algoritmo "vecino más cercano": desde un punto inicial, ordena los items
 * yendo siempre al más cercano. Heurístico simple para TSP, suficiente para 10-30 paradas.
 *
 * @param {{lat,lng}} inicio Punto de partida
 * @param {Array<{id, lat, lng, ...}>} items
 * @returns Array reordenado, con _distance (metros desde anterior)
 */
export function ordenarPorVecinoCercano(inicio, items) {
  const conGeo = items.filter(i => i.lat != null && i.lng != null)
  const sinGeo = items.filter(i => i.lat == null || i.lng == null)
  const restantes = [...conGeo]
  const orden = []
  let actual = inicio
  while (restantes.length > 0) {
    let bestIdx = 0
    let bestD = Infinity
    for (let i = 0; i < restantes.length; i++) {
      const d = distanciaMetros(actual, restantes[i])
      if (d < bestD) { bestD = d; bestIdx = i }
    }
    const next = restantes.splice(bestIdx, 1)[0]
    orden.push({ ...next, _distance: bestD })
    actual = next
  }
  // Sin geo van al final, en su orden original
  return [...orden, ...sinGeo.map(i => ({ ...i, _distance: null }))]
}

/**
 * Agrupa entregas que están físicamente cerca en una "parada" única.
 * El repartidor para la camioneta y reparte a pie a todos los clientes del grupo.
 *
 * @param {Array} ordenados Items ya ordenados por proximidad
 * @param {number} radioMetros Distancia máxima entre items para considerarlos misma parada (default 80m ~ 1 cuadra)
 * @returns Array de paradas: [{ centroide:{lat,lng}, entregas:[...], distanciaDesdeAnterior, label }]
 */
export function agruparEnParadas(ordenados, radioMetros = 80) {
  const paradas = []
  for (const e of ordenados) {
    const ultima = paradas[paradas.length - 1]
    // Si no hay paradas, o el item no tiene geo, o está lejos → nueva parada
    if (!ultima || e.lat == null || ultima.centroide.lat == null ||
        distanciaMetros(ultima.centroide, e) > radioMetros) {
      paradas.push({
        centroide: { lat: e.lat, lng: e.lng },
        entregas: [e],
        distanciaDesdeAnterior: e._distance ?? null,
      })
    } else {
      // Misma parada: agregamos y recalculamos centroide
      ultima.entregas.push(e)
      const n = ultima.entregas.filter(x => x.lat != null).length
      const avgLat = ultima.entregas.filter(x => x.lat != null).reduce((s, x) => s + x.lat, 0) / n
      const avgLng = ultima.entregas.filter(x => x.lat != null).reduce((s, x) => s + x.lng, 0) / n
      ultima.centroide = { lat: avgLat, lng: avgLng }
    }
  }
  // Etiqueta legible: "PARADA N · Barrio · Calle"
  paradas.forEach((p, i) => {
    const barrios = [...new Set(p.entregas.map(e => e.cliente?.barrio).filter(Boolean))]
    p.label = barrios.join(' / ')
    p.numero = i + 1
  })
  return paradas
}

/** Distancia total de la ruta (incluyendo retorno opcional al inicio). */
export function distanciaTotal(inicio, ordenados, conRetorno = false) {
  if (ordenados.length === 0) return 0
  let total = distanciaMetros(inicio, ordenados[0])
  for (let i = 1; i < ordenados.length; i++) {
    if (ordenados[i].lat != null && ordenados[i-1].lat != null) {
      total += distanciaMetros(ordenados[i-1], ordenados[i])
    }
  }
  if (conRetorno && ordenados.length > 0 && ordenados[ordenados.length-1].lat != null) {
    total += distanciaMetros(ordenados[ordenados.length-1], inicio)
  }
  return total
}
